import { useFormContext } from "./formElements";
import { Button, Form, InputGroup, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

/**
 * StairwellTables (type: "table_list")
 * -------------------------------------------------------
 * Lista tabel tworzonych z `element.template`:
 * - edycja nazwy (label) każdej tabeli
 * - edycja wszystkich komórek
 * - dodawanie / usuwanie wierszy (pięter)
 * - "Dodaj tabelę klatki" wstawia kopię template na koniec listy
 * -------------------------------------------------------
 * Kształt tabeli:
 * {
 *   label: string,
 *   type: "table",
 *   columnGroups?: Array<{ label, startColumn, span }>,
 *   columns: Array<{ label, type, value, hideInPdf }>,
 *   value: Array<Array<{ label, type, value }>>
 * }
 */

const clone = (obj) => JSON.parse(JSON.stringify(obj));

// pusty wiersz zbudowany z definicji kolumn tabeli
const emptyRow = (columns) =>
  columns.map((column) => {
    const cell = { label: column.label, type: column.type, value: column.value ?? "" };
    if (column.sum) cell.sum = true;
    if (column.readonly) cell.readonly = true;
    return cell;
  });

const truthy = (v) => v === true || v === "true";

// flaga ustawiona na komórce LUB na definicji kolumny; type "sum" też oznacza sumę
const cellFlag = (cell, column, flag) =>
  truthy(cell?.[flag]) ||
  truthy(column?.[flag]) ||
  (flag === "sum" && (cell?.type === "sum" || column?.type === "sum"));
const cellType = (cell, column) => cell?.type ?? column?.type;

// liczba zmiennoprzecinkowa w trakcie wpisywania (dopuszcza "", "-", "1.", "1,5")
const NUMBER_INPUT = /^-?\d*([.,]\d*)?$/;

const parseNumber = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") return null;
  const num = parseFloat(String(value).replace(",", "."));
  return Number.isFinite(num) ? num : null;
};

// komórki sum=true dostają średnią z wypełnionych komórek number w tym wierszu;
// komórka z ręcznie wpisaną wartością (manual=true) nie jest nadpisywana
const recalcRow = (row, columns) => {
  const numbers = row
    .filter((cell, c) => !cellFlag(cell, columns[c], "sum") && cellType(cell, columns[c]) === "number")
    .map((cell) => parseNumber(cell.value))
    .filter((num) => num !== null);

  const average = numbers.length
    ? String(Math.round((numbers.reduce((a, b) => a + b, 0) / numbers.length) * 100) / 100)
    : "";

  return row.map((cell, c) =>
    cellFlag(cell, columns[c], "sum") && !cell.manual ? { ...cell, value: average } : cell
  );
};

// grupa nagłówka, do której należy kolumna (albo null)
const groupForColumn = (groups, columnIndex) =>
  groups.find(
    (g) => columnIndex >= g.startColumn && columnIndex < g.startColumn + g.span
  ) ?? null;

function StairwellTableHeader({ columns, columnGroups }) {
  const groups = Array.isArray(columnGroups) ? columnGroups : [];

  if (groups.length === 0)
    return (
      <thead>
        <tr>
          {columns.map((column, c) => (
            <th key={c} className="text-center align-middle">
              {column.label}
            </th>
          ))}
          <th />
        </tr>
      </thead>
    );

  // pierwszy wiersz: grupy (colSpan) + kolumny spoza grup (rowSpan=2)
  const topRow = [];
  columns.forEach((column, c) => {
    const group = groupForColumn(groups, c);

    if (!group) {
      topRow.push(
        <th key={`col-${c}`} rowSpan={2} className="text-center align-middle">
          {column.label}
        </th>
      );
    } else if (group.startColumn === c) {
      topRow.push(
        <th key={`grp-${c}`} colSpan={group.span} className="text-center align-middle">
          {group.label}
        </th>
      );
    }
  });

  return (
    <thead>
      <tr>
        {topRow}
        <th rowSpan={2} />
      </tr>
      <tr>
        {columns.map((column, c) =>
          groupForColumn(groups, c) ? (
            <th key={c} className="text-center align-middle">
              {column.label}
            </th>
          ) : null
        )}
      </tr>
    </thead>
  );
}

export default function StairwellTables(props) {
  const element = props.value;
  const { onChange } = useFormContext();
  const tables = Array.isArray(element.value) ? element.value : [];

  const commit = (nextTables) => {
    element.value = nextTables;
    onChange({ ...element });
  };

  const updateTable = (tableIndex, updater) =>
    commit(tables.map((t, idx) => (idx === tableIndex ? updater(t) : t)));

  const handleLabelChange = (tableIndex, label) =>
    updateTable(tableIndex, (t) => ({ ...t, label }));

  const handleCellChange = (tableIndex, rowIndex, cellIndex, value) => {
    const table = tables[tableIndex];
    const cell = table.value[rowIndex][cellIndex];
    const column = table.columns[cellIndex];

    const isSum = cellFlag(cell, column, "sum");

    if (cellFlag(cell, column, "readonly")) return;
    if ((isSum || cellType(cell, column) === "number") && !NUMBER_INPUT.test(value)) return;

    // ręczna wartość w komórce sumy blokuje automat; puste pole przywraca liczenie
    const editCell = (cell) => {
      if (!isSum) return { ...cell, value };
      const { manual, ...rest } = cell;
      return value === "" ? rest : { ...rest, value, manual: true };
    };

    updateTable(tableIndex, (t) => ({
      ...t,
      value: t.value.map((row, r) =>
        r !== rowIndex
          ? row
          : recalcRow(
              row.map((cell, c) => (c === cellIndex ? editCell(cell) : cell)),
              t.columns
            )
      ),
    }));
  };

  const handleAddRow = (tableIndex) =>
    updateTable(tableIndex, (t) => ({
      ...t,
      value: [...(t.value ?? []), emptyRow(t.columns)],
    }));

  const handleRemoveRow = (tableIndex, rowIndex) =>
    updateTable(tableIndex, (t) => ({
      ...t,
      value: t.value.filter((_, r) => r !== rowIndex),
    }));

  const handleAddTable = () => {
    if (!element.template) return;

    const table = clone(element.template);
    table.label = `${element.template.label} ${tables.length + 1}`.trim();
    commit([...tables, table]);
  };

  const handleRemoveTable = (tableIndex) =>
    commit(tables.filter((_, idx) => idx !== tableIndex));

  return (
    <div className="p-2">
      {element.label ? <h5>{element.label}</h5> : null}

      {tables.map((table, tIndex) => (
        <div key={tIndex} className="mb-4 border rounded p-2">
          <InputGroup className="mb-2">
            <InputGroup.Text>Nazwa</InputGroup.Text>
            <Form.Control
              value={table.label ?? ""}
              placeholder="Nazwa tabeli"
              onChange={(e) => handleLabelChange(tIndex, e.target.value)}
            />
            <Button
              variant="outline-danger"
              onClick={() => handleRemoveTable(tIndex)}
            >
              Usuń tabelę
            </Button>
          </InputGroup>

          <div className="overflow-auto">
            <Table bordered hover size="sm" className="mb-2">
              <StairwellTableHeader
                columns={table.columns}
                columnGroups={table.columnGroups}
              />
              <tbody>
                {(table.value ?? []).map((row, rIndex) => (
                  <tr key={rIndex}>
                    {recalcRow(row, table.columns).map((cell, cIndex) => {
                      const column = table.columns[cIndex];
                      const isSum = !!cellFlag(cell, column, "sum");
                      const isReadonly = !!cellFlag(cell, column, "readonly");
                      const isNumber = isSum || cellType(cell, column) === "number";

                      return (
                        <td key={cIndex} className="p-0">
                          <Form.Control
                            className={`border-0 rounded-0 text-center${isReadonly || (isSum && !cell.manual) ? " bg-light" : ""}`}
                            title={isSum ? (cell.manual ? "Wartość wpisana ręcznie — wyczyść pole, aby liczyć automatycznie" : "Średnia liczona automatycznie") : undefined}
                            type="text"
                            inputMode={isNumber ? "decimal" : undefined}
                            readOnly={isReadonly}
                            tabIndex={isReadonly ? -1 : undefined}
                            value={cell.value ?? ""}
                            placeholder={isReadonly ? "" : cell.label}
                            onChange={(e) =>
                              handleCellChange(tIndex, rIndex, cIndex, e.target.value)
                            }
                          />
                        </td>
                      );
                    })}
                    <td className="p-0 text-center align-middle">
                      <Button
                        variant="link"
                        className="text-danger p-1"
                        title="Usuń piętro"
                        onClick={() => handleRemoveRow(tIndex, rIndex)}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleAddRow(tIndex)}
          >
            <FontAwesomeIcon icon={faPlus} /> Dodaj piętro
          </Button>
        </div>
      ))}

      <Button variant="primary" onClick={handleAddTable} disabled={!element.template}>
        <FontAwesomeIcon icon={faPlus} /> Dodaj tabelę klatki
      </Button>
    </div>
  );
}
